import { PrinterOutlined } from '@ant-design/icons';
import { Button, ConfigProvider, Form, Space, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Fixed2 } from '../../../../common/utils/common.utils';
import { excelDateRangeFormat } from '../../../../common/utils/excelDateRangeFormat';
import DownloadExcelReport from '../../../../components/common/ExcelReport';
import CommonViewReport from '../../../../components/common/Invoice/ViewInvoiceInfo/CommonViewReport';
import ReportsHeader from '../../../../components/common/ReportsHeader';
import BreadCrumb from '../../../../components/common/breadCrumb/BreadCrumb';
import { loadingIndicator } from '../../../../components/common/spinner/LoadingIndicator';
import { useGetTicketWiseProfitQuery } from '../endpoints/profitLossEndpoints';
import { ITicketWiseProfitLossFormDataType } from '../types/ticketWiseProfitLossTypes';
import { ticketWiseProfitColumn } from '../utils/ticketWiseProfitColumn';

const Ticket_Wise_Profit_loss = () => {
  const [paramData, setParamData] = useState<{
    date_range: string;
    id: number | string;
  }>();
  const [form] = Form.useForm();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });

  const handlePaginationChange = (current: number, pageSize: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      current,
      pageSize,
    }));
  };

  let airticket_profit: number = 0;

  const [dateRange, setDateRange] = useState(``);
  const [ticketId, setTicketId] = useState('');

  const { data, isLoading, isFetching } = useGetTicketWiseProfitQuery(
    {
      ...pagination,
      date_range: dateRange,
      ticket_id: ticketId,
    },
    { skip: !dateRange || !ticketId }
  );

  if (data?.data) {
    for (let i = 0; i < data.data.length; i++) {
      const element = data?.data[i];

      airticket_profit += Number(element.airticket_profit);
    }
  }
  const onFinish = async (values: ITicketWiseProfitLossFormDataType) => {
    const setFrom_date = dayjs(values.date_range[0]).format('YYYY-MM-DD');
    const setTo_date = dayjs(values.date_range[1]).format('YYYY-MM-DD');
    const date_range = `from_date=${setFrom_date}&to_date=${setTo_date}`;

    setDateRange(date_range);
    setTicketId(values.ticket_id);
  };

  useEffect(() => {
    setParamData({
      date_range: `${dateRange}`,
      id: '',
    });
  }, [dateRange]);

  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Monthly_Report`,
  });

  // @HANDLE CONTENTS
  const jsx_com = (
    <div>
      <ConfigProvider>
        <Table
          size='small'
          bordered
          columns={ticketWiseProfitColumn({ pagination, paramData })}
          dataSource={data?.data}
          loading={{
            spinning: isLoading || isFetching,
            indicator: loadingIndicator,
          }}
          pagination={
            data?.count !== undefined && data?.count < 20
              ? false
              : {
                  ...pagination,
                  total: data?.count,
                  showSizeChanger: true,
                  pageSizeOptions: ['20', '50', '100', '200', '500'],
                  onChange: handlePaginationChange,
                }
          }
          summary={(_) => {
            let salesTotal = 0;
            let purchaseTotal = 0;
            let profitLossTotal = 0;

            _.forEach(({ airticket_client_price, airticket_purchase_price, airticket_profit }) => {
              salesTotal += Number(airticket_client_price);
              purchaseTotal += Number(airticket_purchase_price);
              profitLossTotal += Number(airticket_profit);
            });

            return (
              <>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={8}>
                    {' '}
                    <Typography
                      style={{
                        textAlign: 'right',
                        marginRight: '10px',
                        fontWeight: 'bold',
                      }}
                    >
                      Total:
                    </Typography>
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={9} align='right'>
                    <Typography
                      style={{
                        textAlign: 'right',
                        marginRight: '5px',
                        fontWeight: 'bold',
                      }}
                    >
                      {Fixed2(salesTotal || 0)}
                    </Typography>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9} align='right'>
                    <Typography
                      style={{
                        textAlign: 'right',
                        marginRight: '5px',
                        fontWeight: 'bold',
                      }}
                    >
                      {Fixed2(purchaseTotal || 0)}
                    </Typography>
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={0}>
                    <Typography
                      style={{
                        textAlign: 'right',
                        marginRight: '5px',
                        fontWeight: 'bold',
                      }}
                    >
                      {Fixed2(profitLossTotal || 0)}
                    </Typography>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={0} />
                </Table.Summary.Row>
              </>
            );
          }}
        />
      </ConfigProvider>
    </div>
  );

  // @HANDLE PRINT WITH HEADER FOOTER
  const print_content = (
    <div hidden>
      <CommonViewReport
        children={jsx_com}
        printRef={componentRef}
        title={{ info_title: '', title: 'Ticket Wise Profit Loss Report' }}
        extraInfo={{
          date:
            paramData?.date_range
              ?.split('&')
              ?.map((param) => dayjs(param.split('=')[1])?.format('DD-MM-YYYY'))
              ?.join(' to ') || '',
        }}
      />
    </div>
  );

  return (
    <>
      <BreadCrumb arrOfOption={['Reports', 'ProfitLoss', 'Ticket Wise Profit Loss Report']} />
      <Space style={{ marginBottom: '1rem' }}>
        <Button type='primary' onClick={handlePrint}>
          <PrinterOutlined />
          Print
        </Button>

        <DownloadExcelReport
          excelName={`ticket_wise_profit_loss_report_${excelDateRangeFormat(dateRange)}`}
          description={`TICKET WISE PROFIT LOSS REPORT`}
          excelTableHead={[
            'Invoice No',
            'Invoice Date',
            'Client Name',
            'PNR',
            'Ticket No',
            'Passport Name',
            'Airline Name',
            'Sales Amount',
            'Purchase Amount',
            'Profit/Loss',
          ]}
          excelData={
            data?.data?.length
              ? data?.data?.map((item: any) => ({
                  'Invoice No': item?.invoice_no || 'N/A',
                  'Invoice Date': item?.invoice_create_date
                    ? dayjs(item?.invoice_create_date).format('YYYY-MM-DD')
                    : 'N/A',
                  'Client Name': item?.client_name || 'N/A',
                  PNR: item?.airticket_pnr || 'N/A',
                  'Ticket No': item?.airticket_ticket_no || 'N/A',
                  'Passport Name': item?.passport_name || 'N/A',
                  'Airline Name': item?.airline_name || 'N/A',
                  'Sales Amount': item?.airticket_client_price
                    ? Fixed2(item?.airticket_client_price)
                    : '0.00',
                  'Purchase Amount': item?.airticket_purchase_price
                    ? Fixed2(item?.airticket_purchase_price)
                    : '0.00',
                  'Profit/Loss': item?.airticket_profit
                    ? Number(item?.airticket_profit).toFixed(2)
                    : '0.00',
                }))
              : []
          }
        />
      </Space>
      <Form
        layout='vertical'
        form={form}
        onFinish={onFinish}
        initialValues={{
          date_range: [dayjs(), dayjs()],
          ticket_id: 'all',
        }}
      >
        <ReportsHeader
          required
          reports_title='Ticket Wise Profit Loss Report'
          showSelectTicket
          date_range
        />
      </Form>

      {/* //----------------//------------// @LEDGER TABLE HERE //----------------//------------// */}
      {jsx_com}

      {/*-----//---------//--------- @PRINTABLE CONTENT -----------//-----------//-----*/}
      {print_content}
    </>
  );
};

export default Ticket_Wise_Profit_loss;
